import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Loader2,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Sparkles,
    CalendarIcon,
    User2,
    CreditCard,
    PhoneCall,
    ListChecks,
    Map,
    KeyRound,
    Truck,
    Boxes,
    Users,
} from "lucide-react";
import { api, SERVICES, MOVE_DAY_TIERS, BRAND } from "@/lib/api";
import { MonerisCheckoutModal } from "@/components/MonerisCheckoutModal";
import { Reveal } from "@/components/site/Reveal";
import { IconSticker } from "@/components/site/Sticker";
import CartoonSticker from "@/components/site/CartoonStickers";
import { HelpCircle } from "lucide-react";

const SERVICE_ICONS = {
    free_consultation: PhoneCall,
    paid_consultation: ListChecks,
    shortlisting: Map,
    viewing_on_behalf: KeyRound,
    move_day_4h: Truck,
    packing_support: Boxes,
    cleaning_support: Sparkles,
    vendor_coordination: Users,
    custom_quote_payment: CreditCard,
};

const STEPS = [
    { id: 1, label: "Service", icon: Sparkles },
    { id: 2, label: "Date & Time", icon: CalendarIcon },
    { id: 3, label: "Your details", icon: User2 },
    { id: 4, label: "Confirm", icon: CreditCard },
];

function formatDate(d) {
    if (!d) return "";
    return d.toISOString().slice(0, 10);
}

export default function Book() {
    const [params] = useSearchParams();
    const initialKey = params.get("service") || "free_consultation";

    const [step, setStep] = useState(1);
    const [serviceKey, setServiceKey] = useState(initialKey);
    const [moveDayTier, setMoveDayTier] = useState(
        initialKey.startsWith("move_day_") ? initialKey : "move_day_4h"
    );
    const [date, setDate] = useState();
    const [slot, setSlot] = useState("");
    const [customAmount, setCustomAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);

    // Payment provider config (Moneris Checkout)
    const [payCfg, setPayCfg] = useState(null);
    // Active Moneris Checkout session: { ticket, environment, jsUrl, bookingId }
    const [mcoSession, setMcoSession] = useState(null);

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        whatsapp: "",
        current_location: "",
        moving_from: "",
        moving_to: "",
        expected_move_date: "",
        in_canada: "",
        contact_method: "email",
        message: "",
    });

    // Derived service values — must be declared before any effect or render code uses them.
    const isMoveDay = useMemo(() => serviceKey.startsWith("move_day_"), [serviceKey]);
    const finalServiceKey = isMoveDay ? moveDayTier : serviceKey;
    const selectedService = SERVICES.find((s) => s.key === serviceKey) || SERVICES[0];
    const isCustomQuote = finalServiceKey === "custom_quote_payment";
    const isPaid =
        finalServiceKey &&
        (SERVICES.find((s) => s.key === finalServiceKey)?.type === "paid" ||
            finalServiceKey.startsWith("move_day_") ||
            isCustomQuote);
    const moveTier = MOVE_DAY_TIERS.find((t) => t.key === moveDayTier);

    // Live availability state
    const [availConfig, setAvailConfig] = useState(null);
    const [slotsForDate, setSlotsForDate] = useState(null);
    const [slotsLoading, setSlotsLoading] = useState(false);

    // Load public availability config once
    useEffect(() => {
        let active = true;
        api.get("/availability/config")
            .then(({ data }) => {
                if (active) setAvailConfig(data);
            })
            .catch(() => {
                if (active)
                    setAvailConfig({
                        working_days: [1, 2, 3, 4, 5, 6, 7],
                        blocked_dates: [],
                        holiday_dates: [],
                        lead_time_hours: 24,
                        max_advance_days: 60,
                    });
            });
        return () => {
            active = false;
        };
    }, []);

    // Load payment provider config once
    useEffect(() => {
        let active = true;
        api.get("/payments/config")
            .then(({ data }) => {
                if (active) setPayCfg(data);
            })
            .catch(() => {
                if (active) setPayCfg({ provider: "moneris_checkout", configured: false });
            });
        return () => {
            active = false;
        };
    }, []);

    // Fetch slots whenever date or service changes
    useEffect(() => {
        if (!date) {
            setSlotsForDate(null);
            return;
        }
        const iso = formatDate(date);
        setSlotsLoading(true);
        setSlot("");
        api
            .get(`/availability/slots?date=${iso}&service_key=${encodeURIComponent(finalServiceKey || "")}`)
            .then(({ data }) => setSlotsForDate(data))
            .catch(() => setSlotsForDate({ open: false, reason: "network_error", slots: [] }))
            .finally(() => setSlotsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, finalServiceKey]);

    function update(field, val) {
        setForm((prev) => ({ ...prev, [field]: val }));
    }

    function validateCustomAmount() {
        const raw = String(customAmount).trim();
        if (!raw) {
            toast.error("Please enter the approved quote amount.");
            return null;
        }
        const val = Number(raw);
        if (!Number.isFinite(val) || val <= 0) {
            toast.error("Please enter a valid payment amount.");
            return null;
        }
        if (val < 1) {
            toast.error("Minimum payment amount is $1 CAD.");
            return null;
        }
        if (val > 5000) {
            toast.error("Maximum payment amount is $5,000 CAD.");
            return null;
        }
        return Math.round(val * 100) / 100;
    }

    function nextStep() {
        if (step === 1 && !serviceKey) {
            toast.error("Pick a service to continue.");
            return;
        }
        if (step === 1 && isCustomQuote && validateCustomAmount() === null) {
            return;
        }
        if (step === 2 && (!date || !slot)) {
            toast.error("Pick a date and time slot.");
            return;
        }
        if (step === 3 && (!form.full_name || !form.email || !form.phone)) {
            toast.error("Please fill in name, email, and phone.");
            return;
        }
        setStep((s) => Math.min(4, s + 1));
    }
    function prevStep() {
        setStep((s) => Math.max(1, s - 1));
    }

    async function submit() {
        let customAmt = null;
        if (isCustomQuote) {
            customAmt = validateCustomAmount();
            if (customAmt === null) return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                service_key: finalServiceKey,
                preferred_date: formatDate(date),
                preferred_time: slot,
                ...(isCustomQuote ? { custom_amount: customAmt } : {}),
            };
            const { data: booking } = await api.post("/bookings", payload);

            if (isPaid) {
                if (!payCfg?.configured) {
                    toast.error(
                        "Online payments are temporarily unavailable. We'll contact you to complete payment."
                    );
                    setDone(booking);
                    return;
                }
                // Moneris Checkout: get a ticket, then open the hosted payment module.
                const { data: mco } = await api.post("/payments/checkout/preload", {
                    booking_id: booking.id,
                });
                setMcoSession({
                    ticket: mco.ticket,
                    environment: mco.environment,
                    jsUrl: mco.js_url || payCfg?.js_url,
                    bookingId: booking.id,
                });
                return;
            }
            setDone(booking);
            toast.success("Request received. Reference " + booking.reference_id);
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message;
            toast.error("Could not submit booking: " + msg);
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <div className="section gradient-warm relative overflow-hidden" data-testid="booking-success">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto max-w-2xl p-10 text-center">
                            <img
                                src={BRAND.logo}
                                alt="AllTask Moves"
                                className="mx-auto h-12 w-auto select-none"
                                draggable={false}
                            />
                            <span className="mx-auto mt-6 inline-flex rounded-full bg-emerald-50 p-3 text-emerald-600 ring-soft">
                                <CheckCircle2 size={32} />
                            </span>
                            <h1 className="mt-6 font-display text-3xl font-semibold leading-tight md:text-4xl">
                                Thank you. Your request has been received.
                            </h1>
                            <p className="mt-4 text-[15px] text-[#475569]">
                                We will review your details and follow up with the next step.
                            </p>
                            <div className="mt-8 rounded-2xl bg-[#FAF6EE] p-5">
                                <p className="text-xs font-semibold uppercase tracking-widest text-[#0E2A47]/60">
                                    Reference
                                </p>
                                <p
                                    className="mt-1 font-display text-2xl font-semibold text-[#F26B1F]"
                                    data-testid="booking-reference"
                                >
                                    {done.reference_id}
                                </p>
                                <p className="mt-3 text-sm text-[#475569]">
                                    Service:{" "}
                                    <span className="font-medium text-[#0E2A47]">
                                        {done.service_label}
                                    </span>
                                </p>
                            </div>
                            {done.service_key === "free_consultation" && (
                                <p
                                    className="mt-4 text-sm text-[#475569]"
                                    data-testid="booking-meet-note"
                                >
                                    A Google Calendar invite with your Google Meet link
                                    will arrive at <span className="font-semibold text-[#0E2A47]">{done.email}</span>{" "}
                                    in a moment.
                                </p>
                            )}
                            <Link to="/" className="btn-secondary mt-8 inline-flex">
                                Back to home
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="book-page">
            {/* HERO */}
            <section className="relative gradient-warm overflow-hidden pb-12 pt-20 md:pb-16 md:pt-24">
                <CartoonSticker name="keys" width={96} rotate={-6} className="hidden xl:inline-flex top-10 right-6" />
                <div className="container-atm relative">
                    <Reveal>
                        <span className="eyebrow">Book consultation</span>
                        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
                            {serviceKey === "free_consultation"
                                ? "Book a free consultation."
                                : `Request: ${selectedService?.title}`}
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 max-w-2xl text-base text-[#475569] md:text-lg">
                            Tell us about your move and we'll help identify the right next
                            step. Free consultations and quote-based services don't require
                            payment.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* PROGRESS + STEPS */}
            <section className="pb-20 pt-2">
                <div className="container-atm">
                    {/* Stepper */}
                    <div className="card-3d mb-8 p-4 md:p-5" data-testid="step-progress">
                        <div className="flex flex-wrap items-center gap-2 md:gap-4">
                            {STEPS.map((s, i) => {
                                const active = step === s.id;
                                const complete = step > s.id;
                                return (
                                    <div key={s.id} className="flex flex-1 items-center gap-2 md:gap-3">
                                        <motion.div
                                            initial={false}
                                            animate={{
                                                scale: active ? 1.05 : 1,
                                            }}
                                            transition={{ duration: 0.35 }}
                                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                                                complete
                                                    ? "bg-emerald-500 text-white"
                                                    : active
                                                      ? "bg-[#F26B1F] text-white"
                                                      : "bg-[#FAF6EE] text-[#0E2A47]/60"
                                            }`}
                                        >
                                            {complete ? <CheckCircle2 size={16} /> : s.id}
                                        </motion.div>
                                        <p
                                            className={`hidden text-xs font-semibold uppercase tracking-widest sm:block ${
                                                active ? "text-[#0E2A47]" : "text-[#0E2A47]/40"
                                            }`}
                                        >
                                            {s.label}
                                        </p>
                                        {i < STEPS.length - 1 && (
                                            <div className="ml-1 hidden h-px flex-1 bg-[#ECE4D3] md:block" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid gap-8 lg:grid-cols-12">
                        {/* MAIN STEP CONTENT */}
                        <div className="lg:col-span-8">
                            <AnimatePresence mode="wait">
                                {step === 1 && (
                                    <Step key="s1">
                                        <h2 className="text-2xl font-semibold md:text-3xl">
                                            Pick a service.
                                        </h2>
                                        <p className="mt-2 text-sm text-[#475569]">
                                            You can change this later, or request a quote for
                                            packing & cleaning.
                                        </p>

                                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                            {SERVICES.filter((s) => !s.hidden).map((s) => {
                                                const Icon = SERVICE_ICONS[s.key] || ListChecks;
                                                const active = s.key === serviceKey;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={s.key}
                                                        onClick={() => setServiceKey(s.key)}
                                                        data-testid={`pick-service-${s.key}`}
                                                        className={`group relative overflow-hidden rounded-2xl border-2 p-5 text-left transition-all ${
                                                            active
                                                                ? "border-[#F26B1F] bg-[#FDF1E7]"
                                                                : "border-[#ECE4D3] bg-white hover:border-[#0E2A47]"
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <span
                                                                className={`inline-flex rounded-xl p-2.5 ${
                                                                    active
                                                                        ? "bg-[#F26B1F] text-white"
                                                                        : "bg-[#0E2A47] text-white"
                                                                }`}
                                                            >
                                                                <Icon size={18} strokeWidth={1.6} />
                                                            </span>
                                                            <span className="font-display text-lg font-semibold text-[#0E2A47]">
                                                                {s.price}
                                                            </span>
                                                        </div>
                                                        <p className="mt-4 text-sm font-semibold text-[#0E2A47]">
                                                            {s.title}
                                                        </p>
                                                        <p className="mt-1 line-clamp-2 text-xs text-[#475569]">
                                                            {s.short}
                                                        </p>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {isMoveDay && (
                                            <div className="mt-6 rounded-2xl bg-[#FAF6EE] p-5">
                                                <p className="text-xs font-bold uppercase tracking-widest text-[#F26B1F]">
                                                    Pick a duration
                                                </p>
                                                <div className="mt-3 grid gap-2 sm:grid-cols-5">
                                                    {MOVE_DAY_TIERS.map((t) => {
                                                        const active = t.key === moveDayTier;
                                                        return (
                                                            <button
                                                                key={t.key}
                                                                type="button"
                                                                onClick={() => setMoveDayTier(t.key)}
                                                                data-testid={`pick-move-tier-${t.key}`}
                                                                className={`rounded-xl border-2 p-3 text-left transition-all ${
                                                                    active
                                                                        ? "border-[#F26B1F] bg-white"
                                                                        : "border-[#ECE4D3] bg-white hover:border-[#0E2A47]"
                                                                }`}
                                                            >
                                                                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#F26B1F]">
                                                                    {t.label}
                                                                </p>
                                                                <p className="font-display text-lg font-semibold text-[#0E2A47]">
                                                                    ${t.amount}
                                                                </p>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {isCustomQuote && (
                                            <div
                                                className="mt-6 rounded-2xl border-2 border-[#F26B1F]/40 bg-[#FDF1E7] p-5"
                                                data-testid="custom-amount-section"
                                            >
                                                <Label
                                                    htmlFor="custom_amount"
                                                    className="text-sm font-semibold text-[#0E2A47]"
                                                >
                                                    Approved quote amount (CAD){" "}
                                                    <span className="text-[#F26B1F]">*</span>
                                                </Label>
                                                <p className="mt-1 text-xs text-[#475569]">
                                                    Enter the exact amount AllTask Moves approved for
                                                    your quote. Minimum $1 · maximum $5,000 CAD.
                                                </p>
                                                <div className="mt-3 flex items-center gap-2">
                                                    <span className="font-display text-xl font-semibold text-[#0E2A47]">
                                                        $
                                                    </span>
                                                    <Input
                                                        id="custom_amount"
                                                        type="number"
                                                        inputMode="decimal"
                                                        min="1"
                                                        max="5000"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        value={customAmount}
                                                        onChange={(e) => setCustomAmount(e.target.value)}
                                                        className="h-12 max-w-[200px] rounded-xl bg-white text-lg font-semibold"
                                                        data-testid="custom-amount-input"
                                                    />
                                                    <span className="text-sm font-medium text-[#475569]">
                                                        CAD
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Step>
                                )}

                                {step === 2 && (
                                    <Step key="s2">
                                        <h2 className="text-2xl font-semibold md:text-3xl">
                                            Pick a date & time.
                                        </h2>
                                        <p className="mt-2 text-sm text-[#475569]">
                                            We'll confirm availability after you submit.
                                        </p>

                                        <div className="mt-6 grid gap-6 lg:grid-cols-2">
                                            <div className="rounded-2xl border border-[#ECE4D3] p-2">
                                                <Calendar
                                                    mode="single"
                                                    selected={date}
                                                    onSelect={setDate}
                                                    disabled={(d) => {
                                                        const today = new Date(new Date().setHours(0, 0, 0, 0));
                                                        if (d < today) return true;
                                                        if (!availConfig) return false;
                                                        const max = new Date(today);
                                                        max.setDate(max.getDate() + (availConfig.max_advance_days || 60));
                                                        if (d > max) return true;
                                                        // ISO weekday: Mon=1..Sun=7
                                                        const iso = d.getDay() === 0 ? 7 : d.getDay();
                                                        if (!(availConfig.working_days || []).includes(iso)) return true;
                                                        const dStr = d.toISOString().slice(0, 10);
                                                        if ((availConfig.blocked_dates || []).includes(dStr)) return true;
                                                        if ((availConfig.holiday_dates || []).includes(dStr)) return true;
                                                        return false;
                                                    }}
                                                    data-testid="booking-calendar"
                                                />
                                                {availConfig && (
                                                    <p className="mt-3 px-2 pb-1 text-xs text-[#475569]">
                                                        Working days only · up to {availConfig.max_advance_days} days ahead. Blocked / holiday dates are hidden.
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <Label className="mb-2 block">Time slot</Label>
                                                {!date && (
                                                    <p className="text-sm text-[#475569]">
                                                        Pick a date to see available slots.
                                                    </p>
                                                )}
                                                {date && slotsLoading && (
                                                    <div className="flex items-center gap-2 text-sm text-[#475569]">
                                                        <Loader2 size={14} className="animate-spin" /> Loading slots…
                                                    </div>
                                                )}
                                                {date && !slotsLoading && slotsForDate && !slotsForDate.open && (
                                                    <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="slots-closed">
                                                        {(() => {
                                                            const r = slotsForDate.reason;
                                                            if (r === "weekend_or_off_day")
                                                                return "Weekends and off days are not bookable. Please pick a Monday–Friday date.";
                                                            if (r === "holiday")
                                                                return "This date is a holiday and is not available. Please pick another date.";
                                                            if (r === "blocked")
                                                                return "This date is blocked by the team. Please pick another date.";
                                                            if (r === "past")
                                                                return "That date has already passed. Please pick a future date.";
                                                            if (r === "too_far")
                                                                return `That date is beyond our ${availConfig?.max_advance_days || 60}-day booking window. Please pick an earlier date.`;
                                                            if (r === "invalid_date")
                                                                return "That date couldn't be read. Please re-select.";
                                                            if (r === "network_error")
                                                                return "We couldn't load slots right now. Please try again in a moment.";
                                                            return "This date isn't available. Please pick another date.";
                                                        })()}
                                                    </div>
                                                )}
                                                {date && !slotsLoading && slotsForDate?.open && (
                                                    <>
                                                        <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
                                                            {(slotsForDate.slots || []).map((s) => (
                                                                <button
                                                                    type="button"
                                                                    key={s.time}
                                                                    onClick={() => s.available && setSlot(s.time)}
                                                                    disabled={!s.available}
                                                                    data-testid={`slot-${s.time.replace(/\s+/g, "-")}`}
                                                                    className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                                                                        !s.available
                                                                            ? "cursor-not-allowed border-[#ECE4D3] bg-[#FAF6EE] text-[#0E2A47]/30 line-through"
                                                                            : slot === s.time
                                                                              ? "border-[#F26B1F] bg-[#FDF1E7] text-[#F26B1F]"
                                                                              : "border-[#ECE4D3] text-[#0E2A47]/80 hover:border-[#0E2A47]"
                                                                    }`}
                                                                >
                                                                    {s.time}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {(slotsForDate.slots || []).every((x) => !x.available) && (
                                                            <p className="mt-3 text-sm text-rose-700">
                                                                All slots taken on this day. Try another date.
                                                            </p>
                                                        )}
                                                        {slotsForDate.service_duration_minutes && (
                                                            <p className="mt-3 text-xs text-[#475569]">
                                                                Approx. duration: <span className="font-semibold text-[#0E2A47]">{slotsForDate.service_duration_minutes} min</span>
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </Step>
                                )}

                                {step === 3 && (
                                    <Step key="s3">
                                        <h2 className="text-2xl font-semibold md:text-3xl">
                                            Tell us about you.
                                        </h2>
                                        <p className="mt-2 text-sm text-[#475569]">
                                            We use these details only to coordinate your move.
                                        </p>

                                        <div className="mt-6 grid gap-5 md:grid-cols-2">
                                            <Field id="full_name" label="Full name" required>
                                                <Input
                                                    id="full_name"
                                                    value={form.full_name}
                                                    onChange={(e) => update("full_name", e.target.value)}
                                                    placeholder="Your full name"
                                                    data-testid="input-full-name"
                                                />
                                            </Field>
                                            <Field id="email" label="Email" required>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={form.email}
                                                    onChange={(e) => update("email", e.target.value)}
                                                    placeholder="you@example.com"
                                                    data-testid="input-email"
                                                />
                                            </Field>
                                            <Field id="phone" label="Phone" required>
                                                <Input
                                                    id="phone"
                                                    value={form.phone}
                                                    onChange={(e) => update("phone", e.target.value)}
                                                    placeholder="+1 647 555 0100"
                                                    data-testid="input-phone"
                                                />
                                            </Field>
                                            <Field id="whatsapp" label="WhatsApp (optional)">
                                                <Input
                                                    id="whatsapp"
                                                    value={form.whatsapp}
                                                    onChange={(e) => update("whatsapp", e.target.value)}
                                                    placeholder="WhatsApp number"
                                                    data-testid="input-whatsapp"
                                                />
                                            </Field>
                                            <Field id="moving_from" label="Moving from">
                                                <Input
                                                    id="moving_from"
                                                    value={form.moving_from}
                                                    onChange={(e) => update("moving_from", e.target.value)}
                                                    placeholder="London, UK"
                                                    data-testid="input-moving-from"
                                                />
                                            </Field>
                                            <Field id="moving_to" label="Moving to">
                                                <Input
                                                    id="moving_to"
                                                    value={form.moving_to}
                                                    onChange={(e) => update("moving_to", e.target.value)}
                                                    placeholder="Toronto / GTA"
                                                    data-testid="input-moving-to"
                                                />
                                            </Field>
                                            <Field id="expected_move_date" label="Expected move date">
                                                <Input
                                                    id="expected_move_date"
                                                    type="date"
                                                    value={form.expected_move_date}
                                                    onChange={(e) => update("expected_move_date", e.target.value)}
                                                    data-testid="input-expected-move-date"
                                                />
                                            </Field>
                                            <Field id="in_canada" label="Are you currently in Canada?">
                                                <Select
                                                    value={form.in_canada}
                                                    onValueChange={(v) => update("in_canada", v)}
                                                >
                                                    <SelectTrigger
                                                        id="in_canada"
                                                        className="h-12 rounded-xl"
                                                        data-testid="input-in-canada"
                                                    >
                                                        <SelectValue placeholder="Select" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="yes">Yes</SelectItem>
                                                        <SelectItem value="no">No</SelectItem>
                                                        <SelectItem value="arriving_soon">
                                                            Arriving soon
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </Field>
                                            <Field id="contact_method" label="Preferred contact method">
                                                <Select
                                                    value={form.contact_method}
                                                    onValueChange={(v) => update("contact_method", v)}
                                                >
                                                    <SelectTrigger
                                                        id="contact_method"
                                                        className="h-12 rounded-xl"
                                                        data-testid="input-contact-method"
                                                    >
                                                        <SelectValue placeholder="Choose" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="email">Email</SelectItem>
                                                        <SelectItem value="phone">Phone call</SelectItem>
                                                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </Field>
                                        </div>
                                        <div className="mt-5">
                                            <Field id="message" label="Message / urgent details">
                                                <Textarea
                                                    id="message"
                                                    value={form.message}
                                                    onChange={(e) => update("message", e.target.value)}
                                                    placeholder="Anything we should know..."
                                                    rows={4}
                                                    data-testid="input-message"
                                                />
                                            </Field>
                                        </div>
                                    </Step>
                                )}

                                {step === 4 && (
                                    <Step key="s4">
                                        <h2 className="text-2xl font-semibold md:text-3xl">
                                            Review and confirm.
                                        </h2>
                                        <p className="mt-2 text-sm text-[#475569]">
                                            {isPaid
                                                ? "Our secure Moneris checkout will open right here to complete payment."
                                                : "No payment is required for this request."}
                                        </p>

                                        <div className="mt-6 grid gap-5 md:grid-cols-2">
                                            <Row
                                                label="Service"
                                                value={
                                                    isCustomQuote
                                                        ? `Custom Quote Payment — $${Number(customAmount || 0).toFixed(2)} CAD`
                                                        : selectedService?.title
                                                }
                                            />
                                            {isMoveDay && (
                                                <Row
                                                    label="Move-day duration"
                                                    value={`${moveTier?.label} — $${moveTier?.amount} CAD`}
                                                />
                                            )}
                                            <Row
                                                label="Date / time"
                                                value={
                                                    date
                                                        ? `${formatDate(date)} · ${slot}`
                                                        : "—"
                                                }
                                            />
                                            <Row label="Name" value={form.full_name} />
                                            <Row label="Email" value={form.email} />
                                            <Row label="Phone" value={form.phone} />
                                            <Row label="Moving from → to" value={`${form.moving_from || "—"} → ${form.moving_to || "—"}`} />
                                            <Row
                                                label="Estimated payable"
                                                value={
                                                    isPaid
                                                        ? isCustomQuote
                                                            ? `$${Number(customAmount || 0).toFixed(2)} CAD`
                                                            : `$${
                                                                  isMoveDay
                                                                      ? moveTier?.amount
                                                                      : SERVICES.find(
                                                                            (s) =>
                                                                                s.key ===
                                                                                finalServiceKey
                                                                        )?.price.replace("$", "")
                                                              } CAD`
                                                        : "Free / Quote"
                                                }
                                            />
                                        </div>
                                    </Step>
                                )}
                            </AnimatePresence>

                            <div className="mt-8 flex items-center justify-between">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={prevStep}
                                    disabled={step === 1}
                                    data-testid="step-back"
                                    className="rounded-full"
                                >
                                    <ArrowLeft size={16} className="mr-1" /> Back
                                </Button>
                                {step < 4 ? (
                                    <Button
                                        type="button"
                                        onClick={nextStep}
                                        className="btn-primary"
                                        data-testid="step-next"
                                    >
                                        Continue <ArrowRight size={16} />
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={submit}
                                        disabled={submitting}
                                        className="btn-primary"
                                        data-testid="booking-submit-btn"
                                    >
                                        {submitting ? (
                                            <Loader2 className="animate-spin" size={18} />
                                        ) : (
                                            <>
                                                {isPaid ? "Continue to Payment" : "Submit Booking"}{" "}
                                                <ArrowRight size={16} />
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* SUMMARY ASIDE */}
                        <aside className="lg:col-span-4">
                            <div className="card-3d sticky top-28 p-7">
                                <p className="text-xs font-bold uppercase tracking-widest text-[#F26B1F]">
                                    Summary
                                </p>
                                <h3 className="mt-2 font-display text-xl font-semibold text-[#0E2A47]">
                                    {selectedService?.title || "Free Consultation"}
                                </h3>
                                <p className="mt-1 text-sm text-[#475569]">
                                    {selectedService?.short}
                                </p>

                                <div className="mt-5 space-y-3 border-t border-[#ECE4D3] pt-5 text-sm">
                                    <SummaryRow label="Price">
                                        <span className="font-semibold text-[#0E2A47]" data-testid="summary-price">
                                            {isMoveDay
                                                ? `$${moveTier?.amount} CAD`
                                                : isCustomQuote
                                                  ? customAmount
                                                      ? `$${Number(customAmount).toFixed(2)} CAD`
                                                      : "Enter amount"
                                                  : selectedService?.price}
                                        </span>
                                    </SummaryRow>
                                    <SummaryRow label="Date">
                                        {date ? formatDate(date) : "—"}
                                    </SummaryRow>
                                    <SummaryRow label="Time">{slot || "—"}</SummaryRow>
                                </div>

                                <div className="mt-5 rounded-2xl bg-[#FAF6EE] p-4 text-xs text-[#475569]">
                                    {isPaid
                                        ? "Paid services use Moneris secure checkout. Cards are processed by Moneris — we never see your card details."
                                        : "Free consultations and quote-based services do not require payment."}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <section className="gradient-cream py-16 md:py-20">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto flex max-w-2xl flex-col items-center gap-4 p-8 text-center md:flex-row md:text-left">
                            <span className="inline-flex rounded-2xl bg-[#0E2A47] p-3 text-white">
                                <HelpCircle size={22} strokeWidth={1.8} />
                            </span>
                            <div className="flex-1">
                                <p className="text-xs font-bold uppercase tracking-widest text-[#F26B1F]">
                                    Questions before you book?
                                </p>
                                <p className="mt-1 font-display text-lg font-semibold text-[#0E2A47] md:text-xl">
                                    Have questions? Visit our FAQ page.
                                </p>
                            </div>
                            <Link
                                to="/faq"
                                data-testid="book-faq-cta"
                                className="btn-secondary inline-flex whitespace-nowrap"
                            >
                                Visit FAQ <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {mcoSession && (
                <MonerisCheckoutModal
                    ticket={mcoSession.ticket}
                    environment={mcoSession.environment}
                    jsUrl={mcoSession.jsUrl}
                    bookingId={mcoSession.bookingId}
                    onClose={() => setMcoSession(null)}
                />
            )}
        </div>
    );
}

function Step({ children }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="card-3d p-7 md:p-9"
        >
            {children}
        </motion.div>
    );
}

function Field({ id, label, required, children }) {
    return (
        <div>
            <Label htmlFor={id} className="text-sm font-medium text-[#0E2A47]">
                {label} {required && <span className="text-[#F26B1F]">*</span>}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="rounded-2xl bg-[#FAF6EE] p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-[#F26B1F]">
                {label}
            </p>
            <p className="mt-1 font-medium text-[#0E2A47]">{value || "—"}</p>
        </div>
    );
}

function SummaryRow({ label, children }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#475569]">{label}</span>
            <span className="text-right font-medium text-[#0E2A47]">{children}</span>
        </div>
    );
}
